import { RouteObject } from 'react-router-dom'

import { JourneyGlobalLayout, JourneyLayoutMaxWidth } from '@cash-web/shared-journey-feature-main'

import { JourneyDefaultError } from '../error/components/JourneyDefaultError'

/**
 * Intelligently merges journey routes based on their paths.
 *
 * Routes with paths starting with 'dl/view/' get wrapped in global > dl/view/* > max-width layouts.
 * Routes with paths starting with 'dl/flow/' or 'dl/scenario/' get wrapped in global layout only.
 *
 * Developers don't need to know the structure - just import routes and merge them.
 *
 * @example
 * ```tsx
 * import { mergeJourneyRoutes, activityItemRoute, flowGetRoute } from '@cash-web/shared-journey-feature-routes'
 *
 * const routes = mergeJourneyRoutes([activityItemRoute, flowGetRoute])
 * <JourneyProvider routes={routes}>
 * ```
 */
export function mergeJourneyRoutes(routes: RouteObject[]): RouteObject[] {
  // Categorize routes by their layout needs based on path
  const flowRoutes: RouteObject[] = []
  const viewRoutes: RouteObject[] = []

  for (const route of routes) {
    // Determine layout based on path prefix
    if (route.path?.startsWith('dl/flow/') || route.path?.startsWith('dl/scenario/')) {
      flowRoutes.push(route)
    } else if (route.path?.startsWith('dl/view/')) {
      // Strip the 'dl/view/' prefix since we'll nest it under dl/view/*
      const pathWithoutPrefix = route.path.replace(/^dl\/view\//, '')
      viewRoutes.push({ ...route, path: pathWithoutPrefix })
    } else {
      // Default to view layout for unknown paths
      viewRoutes.push(route)
    }
  }

  // Build the route tree
  const children: RouteObject[] = [...flowRoutes]

  if (viewRoutes.length > 0) {
    children.push({
      path: 'dl/view/*',
      element: <JourneyLayoutMaxWidth />,
      children: viewRoutes,
    })
  }

  return [
    {
      path: '/*',
      element: <JourneyGlobalLayout fallbackComponent={JourneyDefaultError} />,
      errorElement: <JourneyDefaultError />,
      children,
    },
  ]
}
